import ReactGA from 'react-ga4';

// Types
export interface GAEventParams {
  category?: string;
  label?: string;
  value?: number;
  project_id?: string;
  device_type?: string;
  interaction_type?: string;
  link_type?: string;
  [key: string]: any;
}

// Initialize Google Analytics
export const initGA = (): void => {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

  if (!measurementId) {
    console.warn('Google Analytics Measurement ID not found. Tracking disabled.');
    return;
  }

  try {
    ReactGA.initialize(measurementId, {
      gaOptions: {
        anonymize_ip: true, // Privacy-friendly: anonymize IP addresses
        cookie_flags: 'SameSite=None;Secure', // GDPR compliance
      },
      gtagOptions: {
        send_page_view: false, // We'll manually control page views for SPA
      },
    });

    console.log('Google Analytics initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

// Track page views (for SPA routing)
export const trackPageView = (path: string, title?: string): void => {
  if (!isGAInitialized()) return;

  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title,
    });
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
};

// Track custom events
export const trackEvent = (
  eventName: string,
  params?: GAEventParams
): void => {
  if (!isGAInitialized()) return;

  try {
    // Add common parameters
    const enrichedParams = {
      ...params,
      timestamp: new Date().toISOString(),
      page_location: window.location.pathname,
      page_title: document.title,
    };

    ReactGA.event(eventName, enrichedParams);
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

// Track project views
export const trackProjectView = (
  projectId: string,
  viewType: 'preview' | 'case_study' = 'preview'
): void => {
  trackEvent('project_view', {
    project_id: projectId,
    interaction_type: viewType,
    category: 'Project Interaction',
    label: `${projectId} - ${viewType}`,
  });
};

// Track case study interactions
export const trackCaseStudyOpen = (projectId: string): void => {
  trackEvent('case_study_open', {
    project_id: projectId,
    category: 'Engagement',
    label: projectId,
  });
};

export const trackCaseStudyClose = (projectId: string): void => {
  trackEvent('case_study_close', {
    project_id: projectId,
    category: 'Engagement',
    label: projectId,
  });
};

// Track external link clicks
export const trackExternalLink = (
  linkType: 'github' | 'linkedin' | 'demo',
  projectId?: string
): void => {
  trackEvent('external_link_click', {
    link_type: linkType,
    project_id: projectId,
    category: 'Outbound',
    label: projectId ? `${linkType} - ${projectId}` : linkType,
  });
};

// Track email copy attempts
export const trackEmailCopy = (success: boolean): void => {
  trackEvent('email_copy', {
    interaction_type: success ? 'success' : 'failed',
    category: 'Contact',
    label: success ? 'Email copied successfully' : 'Email copy failed',
  });
};

// Track sidebar interactions
export const trackSidebarToggle = (action: 'open' | 'close'): void => {
  trackEvent(`sidebar_${action}`, {
    category: 'Navigation',
    label: `Sidebar ${action}`,
  });
};

// Track modal navigation
export const trackModalNavigation = (
  action: 'next' | 'prev' | 'close',
  projectId?: string
): void => {
  trackEvent('modal_navigation', {
    modal_action: action,
    project_id: projectId,
    category: 'Navigation',
    label: projectId ? `${action} - ${projectId}` : action,
  });
};

// Track scroll depth
export const trackScrollDepth = (percentage: number, projectId?: string): void => {
  trackEvent('scroll_depth', {
    scroll_percentage: percentage,
    project_id: projectId,
    category: 'Engagement',
    label: `${percentage}% scrolled`,
  });
};

// Track user timing (for performance)
export const trackTiming = (
  category: string,
  variable: string,
  time: number,
  label?: string
): void => {
  if (!isGAInitialized()) return;

  try {
    ReactGA.gtag('event', 'timing_complete', {
      name: variable,
      value: time,
      event_category: category,
      event_label: label,
    });
  } catch (error) {
    console.error('Failed to track timing:', error);
  }
};

// Track exceptions/errors
export const trackException = (
  description: string,
  fatal: boolean = false
): void => {
  if (!isGAInitialized()) return;

  try {
    ReactGA.gtag('event', 'exception', {
      description,
      fatal,
    });
  } catch (error) {
    console.error('Failed to track exception:', error);
  }
};

// Track custom user properties
export const setUserProperties = (properties: Record<string, any>): void => {
  if (!isGAInitialized()) return;

  try {
    ReactGA.gtag('set', 'user_properties', properties);
  } catch (error) {
    console.error('Failed to set user properties:', error);
  }
};

// Helper to check if GA is initialized
const isGAInitialized = (): boolean => {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

  if (!measurementId) {
    return false;
  }

  // Only track in production or when explicitly enabled
  if (process.env.NODE_ENV !== 'production' && !process.env.REACT_APP_ENABLE_GA_DEV) {
    return false;
  }

  return true;
};

// Get device type
export const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Track Core Web Vitals
export const trackWebVitals = (metric: any): void => {
  if (!isGAInitialized()) return;

  try {
    ReactGA.gtag('event', metric.name, {
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      event_category: 'Web Vitals',
      event_label: metric.id,
      non_interaction: true,
    });
  } catch (error) {
    console.error('Failed to track web vitals:', error);
  }
};
